/**
 * Private companies — LinkedIn ingestion/update. Backed by the external
 * "tr-admin-panel-function" Azure Function; the admin API proxies to it so the
 * function `code` secret stays server-side.
 *
 * Upstream endpoints (proxied through /private-companies):
 *   GET  privatecompanies/{id}          → company details
 *   POST privatecompanies/admin/ingest  → create (by name) or update (by id)
 */

/** A private company as returned by the upstream GET endpoint. */
export interface PrivateCompany {
  privateCompanyID: number;
  companyName: string;
  /** Existing LinkedIn URL, or null when none is stored yet. */
  linkedIn: string | null;
}

export interface GetPrivateCompanyResponse {
  company: PrivateCompany;
}

/**
 * Ingest request. Provide `companyID` to update an existing company, or
 * `companyName` to create a new one. `linkedInURL` is always required.
 */
export interface IngestPrivateCompanyRequest {
  companyID?: number;
  companyName?: string;
  linkedInURL: string;
}

/** Result of an ingest (create/update) — mirrors the upstream response. */
export interface IngestPrivateCompanyResponse {
  success: boolean;
  privateCompanyID: number | null;
  errorMessage: string | null;
  enrichmentTriggered: boolean;
}

/**
 * Frontend validation: LinkedIn company URLs must look like
 * https://www.linkedin.com/company/...
 */
export const LINKEDIN_COMPANY_URL_PATTERN =
  /^https:\/\/www\.linkedin\.com\/company\/.+/i;

export function isValidLinkedInCompanyUrl(url: string): boolean {
  return LINKEDIN_COMPANY_URL_PATTERN.test(url.trim());
}
